import { getOperationName } from '@apollo/client/utilities'
import { Button, Image } from 'antd'
import { first } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  ExperienceType,
  GetTimelinePostsDocument,
  PostType,
  SectionType,
  useBasicProfileAboutMeUpdateMutation,
  useCareerProfileUpdateMutation,
  usePostCreateMutation,
  useProfilePhotoUpdateMutation,
  useUserHobbyBatchUpdateMutation,
  useUserInfoHeaderUpdateMutation,
  useUserSkillBatchUpdateMutation,
  useViewerOnboardingUpdateMutation,
} from '@/generated/graphql'
import { routes } from '@/routes'
import { ga, traceEvent } from '@/utils/ga'

import { OnboardingPageProps } from '../onboarding.interface'
import s from '../onboarding.module.less'
import { OnboardingPageLayout } from './onboarding-page-layout.component'

export const OnboardingPage7: React.FC<OnboardingPageProps> = ({ onNext, viewer, userInfo }) => {
  const { t } = useTranslation()
  const history = useHistory()

  const [pageStartTime, setPageStartTime] = React.useState(moment())

  const [onboardingUpdate] = useViewerOnboardingUpdateMutation()
  const [createPost, { loading }] = usePostCreateMutation({
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!],
    onCompleted(data) {
      if (!data) {
        return
      }
      history.push(routes.section.newAgoran())
    },
  })

  const [userSkillUpdate] = useUserSkillBatchUpdateMutation()
  const [userHobbyBatchUpdate] = useUserHobbyBatchUpdateMutation()
  const [userInfoHeaderUpdate] = useUserInfoHeaderUpdateMutation()
  const [aboutMeUpdate] = useBasicProfileAboutMeUpdateMutation()
  const [upsertCareerProfile] = useCareerProfileUpdateMutation()
  const [photoUpdate] = useProfilePhotoUpdateMutation()

  // 页面停留时间
  React.useEffect(() => {
    setPageStartTime(moment())
    return () => {
      const category = traceEvent.onboarding
      ga('event', category.events.expose_time_page, {
        event_category: category.event_category,
        event_label: '7',
        value: moment().diff(pageStartTime, 'ms'),
      })
    }
  }, [])

  const left = (
    <div className={s.page7}>
      <Image
        preview={false}
        src='https://agora-web-cdn.oss-cn-beijing.aliyuncs.com/tale/backgrounds/onboarding-posts.jpg'
      />
    </div>
  )

  const depName = first(viewer.departments.map((dep) => dep.name)) ?? ''
  const isNewAgoran = moment().diff(viewer.createdAt, 'days') < 60

  const basicMessage = `大家好，我是**${viewer.name}**，来自**${depName}**，工作城市在**${t(
    `onboarding.page3.location.${userInfo!.location}`
  )}**。`

  const selectedSkills = userInfo.skills.filter((skill) => skill.isSelected)
  const selectedHobbies = userInfo.hobbies.filter((hobby) => hobby.isSelected)

  const skillMessage =
    selectedSkills.length > 0 ? `技术栈有**${selectedSkills.map((skill) => skill.name).join(', ')}**` : ''
  const hobbyMessage =
    selectedHobbies.length > 0 ? `兴趣爱好是**${selectedHobbies.map((hobby) => hobby.name).join(', ')}**` : ''
  const endMessage = '期待和大家一起为实时互联网的未来而奋斗～'
  const content =
    [basicMessage, [skillMessage, hobbyMessage].filter(Boolean).join(', '), endMessage].filter(Boolean).join(',\n') +
    '。'

  const footer = (
    <>
      <Button
        type='primary'
        loading={loading}
        onClick={() => {
          onboardingUpdate({ variables: { hasOnboarding: true } })
          const category = traceEvent.onboarding
          ga('event', category.events.click_start, {
            event_category: category.event_category,
          })
          onNext!()

          userInfoHeaderUpdate({
            variables: {
              input: {
                location: userInfo.location,
                address: userInfo.address,
                floor: userInfo.floor,
                seatNumber: userInfo.seatNumber,
              },
            },
          })
          aboutMeUpdate({
            variables: {
              aboutMe: {
                value: userInfo.aboutMe,
                isVisible: true,
              },
            },
          })
          if (selectedSkills.length > 0) {
            userSkillUpdate({
              variables: {
                userId: viewer.userId,
                skillIds: selectedSkills.map((skill) => skill.skillId),
              },
            })
          }

          if (selectedHobbies.length > 0) {
            userHobbyBatchUpdate({
              variables: {
                userId: viewer.userId,
                hobbyIds: selectedHobbies.map((hobby) => hobby.hobbyId),
              },
            })
          }

          upsertCareerProfile({
            variables: {
              careerProfile: {
                organization: userInfo.school,
                duration: [userInfo.schoolStartTime, userInfo.schoolEndTime]
                  .map((value) => value.format('YYYY-MM-DD'))
                  .join('_'),
                position: '',
                jobDescription: '',
                experienceType: ExperienceType.Education,
              },
            },
          })

          if (
            moment.isMoment(userInfo.companyStartTime) &&
            moment.isMoment(userInfo.companyEndTime) &&
            userInfo.company
          ) {
            upsertCareerProfile({
              variables: {
                careerProfile: {
                  organization: userInfo.company,
                  duration: [userInfo.companyStartTime, userInfo.companyEndTime]
                    .map((value) => value.format('YYYY-MM-DD'))
                    .join('_'),
                  position: '',
                  jobDescription: '',
                  experienceType: ExperienceType.Career,
                },
              },
            })
          }
          if (userInfo.photo) {
            photoUpdate({
              variables: {
                userId: viewer.userId,
                fileId: userInfo.photo.fileId,
                visible: true,
              },
            })
          }
          if (isNewAgoran) {
            createPost({
              variables: {
                post: {
                  content,
                  sectionId: SectionType.NewAgoran,
                  isMarkdown: true,
                  postType: PostType.Introduction,
                },
              },
            })
          }
        }}
      >
        {t('onboarding.start')}
      </Button>
    </>
  )

  return (
    <OnboardingPageLayout
      header='DISCOVER MORE'
      subHeader={t('onboarding.page4.content')}
      left={left}
      footer={footer}
      percent={7}
    />
  )
}
